import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { PRIORITIES, PRIORITY_ORDER, TASK_ESTADOS, TASK_ESTADO_ORDER, fmtDate, isOverdue } from "../lib/constants";
import { PriorityBadge, Avatar } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ListChecks, Plus, Trash2, CalendarClock, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", responsable_id: "", priority: "media", estado: "pendiente", fecha_limite: "" };

export default function TasksPage() {
  const { canWrite } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/tasks").then((r) => setTasks(r.data)), []);
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      title: t.title, description: t.description || "", responsable_id: t.responsable_id || "",
      priority: t.priority, estado: t.estado, fecha_limite: t.fecha_limite ? t.fecha_limite.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      ...form,
      responsable_id: form.responsable_id || null,
      fecha_limite: form.fecha_limite ? new Date(form.fecha_limite).toISOString() : null,
    };
    try {
      if (editing) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editing ? "Tarea actualizada" : "Tarea creada");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeEstado = async (t, estado) => {
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado } : x)));
    try { await api.put(`/tasks/${t.id}`, { estado }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
  };

  const toggleComplete = async (t) => {
    const estado = t.estado === "completada" ? "pendiente" : "completada";
    await changeEstado(t, estado);
  };

  const del = async (t) => {
    try { await api.delete(`/tasks/${t.id}`); toast.success("Tarea eliminada"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const notify = async (t) => {
    toast.loading("Enviando correo...", { id: "tnotify" });
    try {
      const { data } = await api.post(`/tasks/${t.id}/notify`);
      toast.success(`Correo enviado a ${data.recipients.length} destinatario(s)`, { id: "tnotify" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "tnotify" });
    }
  };

  const grouped = {};
  TASK_ESTADO_ORDER.forEach((e) => (grouped[e] = []));
  tasks.forEach((t) => grouped[t.estado]?.push(t));

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Gestión</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2"><ListChecks className="h-7 w-7" />Tareas</h1>
          <p className="text-sm text-slate-500 mt-1">Tareas independientes, no vinculadas a desarrollos.</p>
        </div>
        {canWrite && (
          <Button data-testid="new-task-btn" onClick={openNew} className="rounded-sm bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-1" /> Nueva tarea
          </Button>
        )}
      </div>

      <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="tasks-board">
        {TASK_ESTADO_ORDER.map((estado) => (
          <div key={estado} className="border border-border bg-white rounded-sm">
            <div className="border-b border-border px-4 py-3 flex items-center justify-between bg-slate-50">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-700">{TASK_ESTADOS[estado].label}</span>
              <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded-sm text-slate-500">{grouped[estado].length}</span>
            </div>
            <div className="p-3 space-y-2 min-h-[120px]">
              {grouped[estado].map((t) => (
                <div key={t.id} data-testid={`task-card-${t.id}`} className="border border-slate-200 rounded-sm p-3 relative">
                  <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: PRIORITIES[t.priority]?.dot }} />
                  <div className="pl-2">
                    <div className="flex items-start justify-between gap-2">
                      <label className="flex items-start gap-2 cursor-pointer flex-1 min-w-0">
                        {canWrite && <Checkbox className="mt-0.5" checked={t.estado === "completada"} onCheckedChange={() => toggleComplete(t)} data-testid={`task-complete-${t.id}`} />}
                        <span className={`text-sm font-semibold leading-snug ${t.estado === "completada" ? "line-through text-slate-400" : "text-slate-900"}`} onClick={() => canWrite && openEdit(t)}>{t.title}</span>
                      </label>
                      {canWrite && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-slate-900 transition-colors duration-200" data-testid={`task-edit-${t.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => notify(t)} className="text-slate-400 hover:text-slate-900 transition-colors duration-200" data-testid={`task-notify-${t.id}`} title="Enviar por correo">
                            <Mail className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => del(t)} className="text-slate-400 hover:text-red-600 transition-colors duration-200" data-testid={`task-delete-${t.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {t.description && <p className="text-xs text-slate-500 mt-1 pl-6 line-clamp-2">{t.description}</p>}
                    <div className="flex items-center justify-between mt-2.5 pl-6">
                      <div className="flex items-center gap-2">
                        <PriorityBadge priority={t.priority} />
                        {t.responsable && <Avatar user={t.responsable} size={20} />}
                      </div>
                      {t.fecha_limite && (
                        <span className={`flex items-center gap-1 text-[10px] font-mono ${isOverdue(t.fecha_limite, t.estado === "completada" ? "en_produccion" : "") ? "text-red-600 font-bold" : "text-slate-400"}`}>
                          <CalendarClock className="h-3 w-3" />{fmtDate(t.fecha_limite)}
                        </span>
                      )}
                    </div>
                    {canWrite && (
                      <Select value={t.estado} onValueChange={(v) => changeEstado(t, v)}>
                        <SelectTrigger className="mt-2 ml-6 h-7 rounded-sm text-xs w-[calc(100%-1.5rem)]" data-testid={`task-estado-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TASK_ESTADO_ORDER.map((e) => <SelectItem key={e} value={e}>{TASK_ESTADOS[e].label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              ))}
              {grouped[estado].length === 0 && <p className="text-xs text-slate-400 text-center py-6">Sin tareas</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">{editing ? "Editar tarea" : "Nueva tarea"}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Tarea independiente del ciclo de desarrollos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Título</Label>
              <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5 rounded-sm" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Responsable</Label>
                <Select value={form.responsable_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, responsable_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="task-responsable-select" className="mt-1.5 rounded-sm"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha límite</Label>
                <Input data-testid="task-fecha-input" type="date" value={form.fecha_limite} onChange={(e) => setForm((f) => ({ ...f, fecha_limite: e.target.value }))} className="mt-1.5 rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="task-priority-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITY_ORDER.map((p) => <SelectItem key={p} value={p}>{PRIORITIES[p].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm((f) => ({ ...f, estado: v }))}>
                  <SelectTrigger data-testid="task-estado-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_ESTADO_ORDER.map((e) => <SelectItem key={e} value={e}>{TASK_ESTADOS[e].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="task-save-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
